"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Users, Globe as Globe2, Church, HeartHandshake, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
interface StatItem {
  id: number;
  value: string;
  label: string;
  description: string;
  iconName: "users" | "church" | "globe" | "heart";
}
const statsData: StatItem[] = [{
  id: 1,
  value: "85M+",
  label: "Adherentes Mundiales",
  description: "Una creciente fraternidad global de creyentes unidos en fe y espíritu en todos los continentes.",
  iconName: "users"
}, {
  id: 2,
  value: "370,000+",
  label: "Iglesias",
  description: "Congregaciones locales activas que echan raíces, celebran cultos y discipulan a las comunidades.",
  iconName: "church"
}, {
  id: 3,
  value: "250+",
  label: "Países y Territorios",
  description: "Llegando a las naciones a través de misiones globales, plantación de iglesias y líderes autóctonos.",
  iconName: "globe"
}, {
  id: 4,
  value: "100+",
  label: "Años de Ministerio y Servicio",
  description: "Demostrando compasión, ayuda social y educación teológica desde nuestra fundación en 1914.",
  iconName: "heart"
}];
export default function Statistics() {
  const prefersReducedMotion = useReducedMotion();
  const getIcon = (name: StatItem["iconName"]) => {
    switch (name) {
      case "users":
        return <Users className="size-6 text-primary" />;
      case "church":
        return <Church className="size-6 text-primary" />;
      case "globe":
        return <Globe2 className="size-6 text-primary" />;
      case "heart":
        return <HeartHandshake className="size-6 text-primary" />;
    }
  };
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="keystatistics" data-nav="light" className="relative overflow-hidden bg-background py-20 md:py-28 text-foreground">
      {}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1768199/img/statistics_overview.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1768199/img/statistics_overview.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1768199/img/statistics_overview.png-wvc-sizes" alt="Fondo de congregación en adoración" loading="lazy" decoding="async" className="h-full w-full object-cover object-center opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/90 to-background" />
      </div>

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="outline" className="mb-4 border-primary/20 bg-primary/10 text-primary">Impacto global</Badge>
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">Una comunidad que llega a todos los rincones</h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">Mediante la plantación fiel de iglesias, la labor humanitaria y un ministerio guiado por el Espíritu, las Asambleas de Dios continúan experimentando un rápido crecimiento mundial y un impacto duradero en la comunidad.</p>
        </div>

        {}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8">
          {statsData.map((stat, i) => <motion.div key={stat.id} variants={itemVariants} data-index={i}>
              <Card className="group relative h-full border-border/60 bg-card/90 shadow-md backdrop-blur-xs transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-lg">
                <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                  <div className="rounded-lg bg-primary/10 p-3">
                    {getIcon(stat.iconName)}
                  </div>
                  <span className="text-4xl font-extrabold tracking-tight text-primary sm:text-5xl">
                    {stat.value}
                  </span>
                </CardHeader>
                <CardContent className="pt-4">
                  <CardTitle className="text-xl font-bold text-card-foreground">
                    {stat.label}
                  </CardTitle>
                  <CardDescription className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {stat.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {}
        <div className="mt-16 flex flex-col items-center justify-center gap-4 text-center sm:flex-row">
          <p className="text-sm font-medium text-muted-foreground">
            ¿Desea conocer más sobre nuestras iniciativas y ministerios?
          </p>
          <Button asChild variant="default" size="default">
            <Link to="/MinistriesPage">
              <span>Explorar ministerios</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}